import React, { FormEvent, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  archivePost,
  deletePost,
  listPosts,
  publishPost,
  recalculateAllReadingTimes,
  recalculatePostReadingTime,
  reindexPost,
  reindexPosts,
  restorePost,
  schedulePost,
  unpublishPost
} from '../../api/blog';
import { BlogPageResponse, ListPostsQuery, PostStatus, PostVisibility, PostSummaryDto } from '../../api/blogTypes';
import { Plus, RefreshCw, Search, CheckCircle2, AlertCircle, X } from 'lucide-react';
import PageHeader from '../../components/shared/PageHeader';
import './BlogPostsPage.css';

type QuickAction =
  | 'publish'
  | 'unpublish'
  | 'archive'
  | 'restore'
  | 'delete'
  | 'schedule'
  | 'recalc-reading-time'
  | 'reindex';

type FeedbackState = {
  type: 'success' | 'error' | 'info';
  message: string;
};

type ScheduleModalState = {
  open: boolean;
  ids: number[];
};

const statusOptions: Array<{ value: PostStatus | ''; label: string }> = [
  { value: '', label: 'Any status' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'DELETED', label: 'Deleted' }
];

const visibilityOptions: Array<{ value: PostVisibility | ''; label: string }> = [
  { value: '', label: 'Any visibility' },
  { value: 'PUBLIC', label: 'Public' },
  { value: 'UNLISTED', label: 'Unlisted' },
  { value: 'PRIVATE', label: 'Private' }
];

const pageSizeOptions = [10, 20, 50, 100];

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unexpected error occurred. Please try again.';
};

const formatShortDate = (value: string | null) => {
  if (!value) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat('en', {
      dateStyle: 'medium'
    }).format(new Date(value));
  } catch (error) {
    console.error('Failed to format date', error);
    return value;
  }
};

const BlogPostsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<PostStatus | ''>('');
  const [visibility, setVisibility] = useState<PostVisibility | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [scheduleModal, setScheduleModal] = useState<ScheduleModalState>({ open: false, ids: [] });
  const [scheduledAt, setScheduledAt] = useState('');
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);

  const isDeletedFilter = status === 'DELETED';

  const queryParams = useMemo<ListPostsQuery>(
    () => ({
      status: isDeletedFilter ? undefined : status || undefined,
      visibility: visibility || undefined,
      q: searchTerm.trim() || undefined,
      page,
      size: pageSize,
      sort: 'published_at,desc',
      deleted: isDeletedFilter ? 'only' : 'false'
    }),
    [isDeletedFilter, page, pageSize, searchTerm, status, visibility]
  );

  const { data, isLoading, isFetching, error } = useQuery<BlogPageResponse<PostSummaryDto>>({
    queryKey: ['blog-posts', queryParams],
    queryFn: () => listPosts(queryParams),
    placeholderData: keepPreviousData
  });

  const posts: PostSummaryDto[] = data?.content ?? [];
  const totalPages = data?.total_pages ?? 0;
  const totalElements = data?.total_elements ?? 0;

  const invalidatePosts = () => {
    void queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
  };

  const closeFeedback = () => setFeedback(null);

  const showFeedback = (type: FeedbackState['type'], message: string) => {
    setFeedback({ type, message });
  };

  const postActionMutation = useMutation({
    mutationFn: async ({ ids, action, scheduledAt: scheduleValue }: { ids: number[]; action: QuickAction; scheduledAt?: string }) => {
      if (ids.length === 0) {
        return;
      }
      for (const id of ids) {
        switch (action) {
          case 'publish':
            await publishPost(id, null);
            break;
          case 'unpublish':
            await unpublishPost(id);
            break;
          case 'archive':
            await archivePost(id);
            break;
          case 'restore':
            await restorePost(id);
            break;
          case 'delete':
            await deletePost(id);
            break;
          case 'schedule':
            if (!scheduleValue) {
              throw new Error('Scheduled date is required');
            }
            await schedulePost(id, { scheduled_at: scheduleValue });
            break;
          case 'recalc-reading-time':
            await recalculatePostReadingTime(id);
            break;
          case 'reindex':
            await reindexPost(id);
            break;
          default:
            throw new Error('Unsupported action');
        }
      }
    },
    onSuccess: (_, variables) => {
      invalidatePosts();
      if (variables.action === 'schedule') {
        setScheduleModal({ open: false, ids: [] });
        setScheduledAt('');
      }
      showFeedback('success', 'Operation completed successfully.');
    },
    onError: (mutationError: unknown) => {
      showFeedback('error', extractErrorMessage(mutationError));
    }
  });

  const globalOperationMutation = useMutation({
    mutationFn: async ({ operation }: { operation: 'recalc-reading-time' | 'reindex' }) => {
      switch (operation) {
        case 'recalc-reading-time':
          await recalculateAllReadingTimes(true);
          break;
        case 'reindex':
          await reindexPosts(true);
          break;
        default:
          throw new Error('Unsupported operation');
      }
    },
    onSuccess: () => {
      invalidatePosts();
      showFeedback('success', 'Mass operation scheduled successfully.');
    },
    onError: (operationError: unknown) => {
      showFeedback('error', extractErrorMessage(operationError));
    }
  });

  const isBusy = postActionMutation.isPending || globalOperationMutation.isPending;

  const openScheduleModal = (ids: number[]) => {
    setScheduleModal({ open: true, ids });
    setScheduledAt('');
  };

  const closeScheduleModal = () => {
    setScheduleModal({ open: false, ids: [] });
    setScheduledAt('');
  };

  const handleScheduleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!scheduledAt) {
      showFeedback('error', 'Please select schedule date and time.');
      return;
    }
    const isoDate = new Date(scheduledAt).toISOString();
    postActionMutation.mutate({ ids: scheduleModal.ids, action: 'schedule', scheduledAt: isoDate });
  };

  const handlePageChange = (nextPage: number) => {
    const safePage = Math.min(Math.max(nextPage, 0), Math.max(totalPages - 1, 0));
    setPage(safePage);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const renderStatusBadge = (post: PostSummaryDto) => {
    const statusClass = (() => {
      switch (post.status) {
        case 'PUBLISHED':
          return 'status-badge status-badge--success';
        case 'SCHEDULED':
          return 'status-badge status-badge--warning';
        case 'ARCHIVED':
          return 'status-badge status-badge--muted';
        case 'DELETED':
          return 'status-badge status-badge--danger';
        case 'DRAFT':
        default:
          return 'status-badge';
      }
    })();
    return <span className={statusClass}>{post.status.toLowerCase()}</span>;
  };

  const renderVisibilityBadge = (post: PostSummaryDto) => {
    const visibilityClass = (() => {
      switch (post.visibility) {
        case 'PUBLIC':
          return 'status-chip status-chip--success';
        case 'UNLISTED':
          return 'status-chip status-chip--warning';
        case 'PRIVATE':
        default:
          return 'status-chip';
      }
    })();
    return <span className={visibilityClass}>{post.visibility.toLowerCase()}</span>;
  };

  const getPrimaryAction = (post: PostSummaryDto): { label: string; action: QuickAction } => {
    switch (post.status) {
      case 'PUBLISHED':
        return { label: 'Unpublish', action: 'unpublish' };
      case 'ARCHIVED':
      case 'DELETED':
        return { label: 'Restore', action: 'restore' };
      case 'SCHEDULED':
      case 'DRAFT':
      default:
        return { label: 'Publish', action: 'publish' };
    }
  };

  const handlePrimaryAction = (post: PostSummaryDto) => {
    const { action } = getPrimaryAction(post);
    postActionMutation.mutate({ ids: [post.id], action });
  };

  const buildSecondaryActions = (post: PostSummaryDto, primaryAction: QuickAction) => {
    const actions: Array<{ label: string; handler: () => void }> = [];
    const addAction = (label: string, action: QuickAction) => {
      if (primaryAction === action) {
        return;
      }
      actions.push({ label, handler: () => postActionMutation.mutate({ ids: [post.id], action }) });
    };

    if (post.status !== 'PUBLISHED') {
      addAction('Publish now', 'publish');
    }
    if (post.status === 'PUBLISHED' || post.status === 'SCHEDULED') {
      addAction('Unpublish', 'unpublish');
    }
    if (post.status === 'ARCHIVED' || post.status === 'DELETED') {
      addAction('Restore', 'restore');
    }
    if (post.status !== 'DELETED') {
      actions.push({ label: 'Schedule…', handler: () => openScheduleModal([post.id]) });
      addAction('Archive', 'archive');
    }
    addAction('Delete', 'delete');
    addAction('Recalculate reading time', 'recalc-reading-time');
    addAction('Reindex', 'reindex');

    return actions;
  };

  // Counts per status from currently loaded page
  const statusCounts: Record<string, number> = {};
  for (const p of posts) {
    statusCounts[p.status] = (statusCounts[p.status] ?? 0) + 1;
  }
  const totalCount = data?.total_elements ?? posts.length;

  return (
    <>
      <PageHeader
        title="Blog"
        subtitle={<>Manage posts · <span className="hf-mono">{totalCount}</span> total</>}
        actions={(
          <>
            <button
              type="button"
              className="hf-btn"
              onClick={() => globalOperationMutation.mutate({ operation: 'reindex' })}
              disabled={isBusy}
            >
              <RefreshCw size={14} /> Reindex all
            </button>
            <button
              type="button"
              className="hf-btn primary"
              onClick={() => navigate('/blog/posts/new')}
            >
              <Plus size={14} /> New post
            </button>
          </>
        )}
      />

      <div className="hf-content">
      {feedback && (
        <div className={`hf-banner ${feedback.type === 'success' ? 'success' : feedback.type === 'error' ? 'danger' : ''}`}>
          {feedback.type === 'success' && <CheckCircle2 size={14} style={{ color: 'var(--success)' }} />}
          {feedback.type === 'error' && <AlertCircle size={14} style={{ color: 'var(--danger)' }} />}
          <span>{feedback.message}</span>
          <button type="button" onClick={closeFeedback} className="hf-btn ghost sm" style={{ marginLeft: 'auto' }}><X size={12} /></button>
        </div>
      )}

      {/* Status filter pills */}
      <div className="hf-toolbar">
        <div className="hf-tabs" role="tablist" aria-label="Status filter">
          <button
            type="button"
            className={status === '' ? 'on' : ''}
            onClick={() => { setStatus(''); setPage(0); }}
          >
            All <span className="ct">{totalCount}</span>
          </button>
          {statusOptions.slice(1).map(opt => (
            <button
              key={opt.value}
              type="button"
              className={status === opt.value ? 'on' : ''}
              onClick={() => { setStatus(opt.value as PostStatus); setPage(0); }}
            >
              {opt.label} <span className="ct">{statusCounts[opt.value as string] ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="hf-search" style={{ flex: '1 1 220px', marginLeft: 'auto' }}>
          <Search size={14} />
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => { setSearchTerm(event.target.value); setPage(0); }}
            placeholder="title or slug"
          />
        </div>
        <select
          className="hf-select"
          style={{ maxWidth: 160 }}
          value={visibility}
          onChange={(event) => { setVisibility(event.target.value as PostVisibility | ''); setPage(0); }}
        >
          {visibilityOptions.map(opt => (
            <option key={opt.label} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          className="hf-select"
          style={{ maxWidth: 90 }}
          value={pageSize}
          onChange={(event) => handlePageSizeChange(Number(event.target.value))}
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>{size}/page</option>
          ))}
        </select>
      </div>

      <section className="blog-page__table" aria-live={isFetching ? 'polite' : 'off'}>
        {isLoading ? (
          <div className="blog-page__placeholder">Loading posts…</div>
        ) : error ? (
          <div className="blog-page__placeholder blog-page__placeholder--error">
            Unable to load posts. Please try again.
          </div>
        ) : posts.length === 0 ? (
          <div className="blog-page__placeholder">No posts match the current filters.</div>
        ) : (
          <table className="blog-table">
            <thead>
              <tr>
                <th scope="col">Post</th>
                <th scope="col">Status</th>
                <th scope="col">Visibility</th>
                <th scope="col">Dates</th>
                <th scope="col" className="blog-table__actions-heading">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => {
                const primary = getPrimaryAction(post);
                const secondaryActions = buildSecondaryActions(post, primary.action);
                const publishedLabel = formatShortDate(post.published_at);
                const readingTimeLabel =
                  typeof post.reading_time_minutes === 'number' && post.reading_time_minutes > 0
                    ? `${post.reading_time_minutes} min read`
                    : null;
                return (
                  <tr key={post.id}>
                    <td>
                      <div className="blog-table__post">
                        <Link to={`/blog/posts/${post.id}`} className="blog-table__title">
                          {post.title}
                        </Link>
                        <p className="blog-table__excerpt">{post.excerpt || 'No excerpt provided.'}</p>
                        <span className="blog-table__slug">/{post.slug || '—'}</span>
                      </div>
                    </td>
                    <td>{renderStatusBadge(post)}</td>
                    <td>{renderVisibilityBadge(post)}</td>
                    <td>
                      <div className="blog-table__meta">
                        <span>Published: {publishedLabel}</span>
                        {readingTimeLabel ? <span>{readingTimeLabel}</span> : null}
                      </div>
                    </td>
                    <td className="blog-table__actions">
                      <button
                        type="button"
                        className="blog-button blog-button--ghost"
                        onClick={() => handlePrimaryAction(post)}
                        disabled={isBusy}
                      >
                        {primary.label}
                      </button>
                      <Link to={`/blog/posts/${post.id}`} className="blog-button blog-button--link">
                        Open editor
                      </Link>
                      {secondaryActions.length > 0 ? (
                        <details className="blog-actions-menu">
                          <summary aria-label="More actions">More</summary>
                          <div className="blog-actions-menu__content">
                            {secondaryActions.map((item) => (
                              <button key={item.label} type="button" onClick={item.handler} disabled={isBusy}>
                                {item.label}
                              </button>
                            ))}
                          </div>
                        </details>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <footer className="blog-page__footer">
        <div className="blog-page__count">Total posts: {totalElements}</div>
        <div className="blog-pagination">
          <button type="button" onClick={() => handlePageChange(page - 1)} disabled={isBusy || page <= 0}>
            ← Previous
          </button>
          <span>
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => handlePageChange(page + 1)}
            disabled={isBusy || totalPages === 0 || page >= totalPages - 1}
          >
            Next →
          </button>
        </div>
      </footer>

      {scheduleModal.open && (
        <div className="blog-modal-overlay" role="presentation" onClick={closeScheduleModal}>
          <div
            className="blog-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="blog-modal__header">
              <h2 className="blog-modal__title" id="schedule-modal-title">
                Schedule publication
              </h2>
              <p className="blog-modal__subtitle">Pick a date and time for automatic publishing.</p>
            </div>
            <form onSubmit={handleScheduleSubmit}>
              <div className="blog-modal__body">
                <label htmlFor="scheduled-at" className="blog-control__label">
                  Publish at
                </label>
                <input
                  id="scheduled-at"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(event) => setScheduledAt(event.target.value)}
                  required
                />
              </div>
              <div className="blog-modal__footer">
                <button type="button" className="blog-button blog-button--ghost" onClick={closeScheduleModal}>
                  Cancel
                </button>
                <button type="submit" className="blog-button blog-button--primary" disabled={postActionMutation.isPending}>
                  Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default BlogPostsPage;
